import React, { useState } from "react";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import { TokenRow } from "./TokenRow";
import { TokenListProps } from "components/entities";
import { Collapsible } from "components/common/Collapsible";
import { SectionRule } from "components/common/SectionRule";
import { MetaAddButton } from "components/main/datasets/DatasetMetaSection";
import { ListEmptyShell } from "components/main/overview/ListEmptyShell";
import { ACCESS_LIST_MAX_HEIGHT } from "components/main/pages/pageColumn";

/// Personal access tokens section. Mirrors the Datasets / Teams / Connections
/// shell: a `SectionRule` header over a flat-row list, with a trailing count +
/// "New token" dashed pill flush right. Only the rows scroll — the header and
/// footer stay fixed.
export const TokenList: React.FC<TokenListProps> = ({
  tokens,
  handleRevokeToken,
  onGenerate,
}) => {
  // List-owned so expanding one token collapses any other.
  const [openId, setOpenId] = useState<string | null>(null);
  const [hideInactive, setHideInactive] = useState(false);

  const inactiveCount = tokens.filter((t) => t.get_status !== "active").length;
  const visibleTokens = hideInactive
    ? tokens.filter((t) => t.get_status === "active")
    : tokens;

  if (tokens.length === 0) {
    return (
      <ListEmptyShell
        icon={<VpnKeyOutlinedIcon sx={{ fontSize: 14 }} />}
        label="Personal access tokens"
        hint="Create a token to access the Mycorr API from scripts and the SDK."
        countLabel="0 tokens"
        addLabel="New token"
        onAdd={onGenerate}
      />
    );
  }

  return (
    <div className="flex flex-col min-h-0">
      <SectionRule
        inset
        icon={<VpnKeyOutlinedIcon sx={{ fontSize: 14 }} />}
        label="Personal access tokens"
        action={
          inactiveCount > 0
            ? {
                label: hideInactive ? "Show all" : "Hide expired and revoked",
                onClick: () => {
                  setOpenId(null);
                  setHideInactive((v) => !v);
                },
                testId: "toggle-inactive-tokens",
              }
            : undefined
        }
      />

      <div
        className="min-h-0 overflow-y-auto overflow-x-hidden scrollbar-overlay-hover"
        style={{ maxHeight: ACCESS_LIST_MAX_HEIGHT }}
      >
        {/* Filtered rows stay mounted and collapse to zero height, so the list
            shrinks under the toggle instead of snapping to the new set. */}
        {tokens.map((token) => (
          <Collapsible
            key={token.get_token_id}
            open={!hideInactive || token.get_status === "active"}
            fade
          >
            <TokenRow
              token={token}
              handleRevokeToken={handleRevokeToken}
              expanded={openId === token.get_token_id}
              onExpandedChange={(next) =>
                setOpenId(next ? token.get_token_id : null)
              }
            />
          </Collapsible>
        ))}
        <Collapsible open={visibleTokens.length === 0} fade>
          <div className="flex items-center gap-2 item-height px-1 text-small text-fg-muted italic">
            <span className="w-5 shrink-0" />
            No active tokens.
          </div>
        </Collapsible>
      </div>

      {/* Flush right so "New token" lines up with the list's right edge. */}
      <div className="flex items-center mt-4">
        <span
          className="font-mono text-fg-muted"
          style={{
            fontSize: "var(--font-size-x-small)",
            letterSpacing: "0.04em",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {`${visibleTokens.length} ${
            visibleTokens.length === 1 ? "token" : "tokens"
          }`}
          {hideInactive && ` · ${inactiveCount} hidden`}
        </span>
        <span className="ml-auto">
          <MetaAddButton label="New token" onClick={onGenerate} />
        </span>
      </div>
    </div>
  );
};
